import useAPI from "@/services/api/hook";
import { ExtendedUseQueryResult } from "@/services/api/types";
import { useEffect } from "react";
import { useMessagingContext } from "./MessagingContext";

/**
 * Hook to access messaging functionality.
 * Uses a shared WebSocket connection via MessagingContext to prevent duplicate connections.
 *
 * @param onThoughtMessage - Optional callback for thought/progress messages
 * @param onResponseReceived - Optional callback when a response is received for a pending request
 * @param onQuestionResent - Optional callback when one of the user's questions is being retried, from any tab or from Slack
 */
export const useMessaging = (
  onThoughtMessage?: (thought: string) => void,
  onResponseReceived?: (requestId: string) => void,
  onQuestionResent?: (questionId: string) => void
) => {
  const {
    messages,
    sendMessageViaWebSocket,
    cancelPendingRequest,
    isConnected,
    updateMessageStatus,
    setOnThoughtMessage,
    setOnResponseReceived,
    setOnQuestionResent
  } = useMessagingContext();

  // Register callbacks when they change
  useEffect(() => {
    if (onThoughtMessage) {
      setOnThoughtMessage(onThoughtMessage);
    }
    return () => {
      // Only clear if this component set the callback
      if (onThoughtMessage) {
        setOnThoughtMessage(undefined);
      }
    };
  }, [onThoughtMessage, setOnThoughtMessage]);

  useEffect(() => {
    if (onResponseReceived) {
      setOnResponseReceived(onResponseReceived);
    }
    return () => {
      // Only clear if this component set the callback
      if (onResponseReceived) {
        setOnResponseReceived(undefined);
      }
    };
  }, [onResponseReceived, setOnResponseReceived]);

  useEffect(() => {
    if (onQuestionResent) {
      setOnQuestionResent(onQuestionResent);
    }
    return () => {
      // Only clear if this component set the callback
      if (onQuestionResent) {
        setOnQuestionResent(undefined);
      }
    };
  }, [onQuestionResent, setOnQuestionResent]);

  return {
    messages,
    sendMessageViaWebSocket,
    cancelPendingRequest,
    isConnected,
    updateMessageStatus
  };
};

export const useSuggestedQuestions = (): ExtendedUseQueryResult<string[]> =>
  useAPI("messaging/suggestions");
